import { bell, crack, footprint, sabre, shards, slash, star } from '../kit';
import { type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * ดาบค้อนช่างใหญ่ (หินระฆัง, hard): the master caster's hammer-sabre. A broad dark-iron blade with a
 * bronze collar, blunt weight rather than edge: impact ticks, split stone, a cast bell for the guard.
 */

const IRON = '#b8c0cc';
const IRON_DK = '#6a7484';
const COLLAR = '#d89a48';
const GOLD = '#ffcf5a';
const GOLD_LINE = '#c8862a';
const HUM = '#fff0a8';
const STONE = '#a49e92';
const STONE_HI = '#d8d2c4';

const hammerSabre = (p: Pix, x0: number, y0: number, x1: number, y1: number, curve = -1.6) =>
  sabre(p, x0, y0, x1, y1, { hilt: COLLAR, blade: IRON, w: 5, curve, edge: '#eef2f8' });

/** Short impact ticks fanning up from (cx,cy). */
function impact(p: Pix, cx: number, cy: number, r: number, col = HUM): void {
  for (const a of [-2.6, -2.0, -1.57, -1.1, -0.5]) {
    p.line(cx + Math.cos(a) * r, cy + Math.sin(a) * r, cx + Math.cos(a) * (r + 3), cy + Math.sin(a) * (r + 3), col);
  }
}

registerIcons('skill', {
  // One heavy straight chop, no flourish.
  'fm-subdue': (p) => {
    hammerSabre(p, 23, 3, 9, 26, 1.2);
    p.rect(3, 28, 22, 2, STONE);
    impact(p, 9, 27, 3);
  },
  // The whole body's weight on the blade: a cast bell cracks under it.
  'fm-heavy': (p) => {
    bell(p, 12, 13, 15, STONE, { band: STONE_HI, w: 16 });
    crack(p, [
      [12, 13],
      [10, 18],
      [13, 21],
      [11, 27],
    ], '#4a4038', 2);
    hammerSabre(p, 26, 1, 12, 13, 1);
    shards(p, 12, 19, 12, STONE_HI, 5, 0.3);
  },
  // Across, then down: a cross of two cuts.
  'fm-cross': (p) => {
    p.rect(3, 13, 26, 3, IRON_DK).rect(3, 14, 26, 1, HUM);
    p.rect(15, 3, 3, 26, IRON_DK).rect(16, 3, 1, 26, HUM);
    hammerSabre(p, 4, 28, 13, 19, -1);
    star(p, 16, 14, 3, '#ffffff');
  },
  // The blade spun into a guarding wheel all round.
  'fm-wheel': (p) => {
    slash(p, 16, 16, 13, 0, Math.PI * 2, GOLD_LINE, 3);
    for (let k = 0; k < 4; k++) {
      const a = (k * Math.PI) / 2 + 0.4;
      p.line(16, 16, 16 + Math.cos(a) * 10, 16 + Math.sin(a) * 10, IRON_DK);
    }
    hammerSabre(p, 11, 21, 27, 6, -1);
    p.ell(16, 16, 2, 2, COLLAR);
  },
  // One stride and the blade presses down: a footfall, a falling cut, dazed sparks.
  'fm-stride': (p) => {
    footprint(p, 3, 21, STONE_HI);
    hammerSabre(p, 27, 4, 15, 26, 1);
    p.rect(9, 28, 20, 2, STONE);
    star(p, 24, 22, 2, HUM);
    star(p, 28, 17, 1, HUM);
  },
  // The golden bell closes over the whole body.
  'fm-bell': (p) => {
    bell(p, 18, 6, 21, GOLD, { band: GOLD_LINE });
    hammerSabre(p, 18, 23, 18, 9, 0);
    p.ring(18, 28, 11, 2, HUM, 0, Math.PI);
  },
  // The blade planted in the ground; a still mind rising like incense.
  'fm-zen': (p) => {
    p.rect(4, 27, 24, 2, STONE).rect(6, 26, 20, 1, STONE_HI);
    hammerSabre(p, 18, 8, 18, 28, 0);
    p.path([
      [12, 20],
      [10, 15],
      [13, 11],
      [11, 6],
      [13, 3],
    ], HUM, 1);
    p.ring(18, 9, 5, 5, GOLD, Math.PI, Math.PI * 2);
  },
  // A thousand-weight cut: the ground splits wide under one falling blade.
  'fm-vanquish': (p) => {
    p.poly([
      [3, 24],
      [29, 24],
      [29, 29],
      [3, 29],
    ], STONE);
    p.poly([
      [11, 24],
      [16, 29],
      [21, 24],
      [17, 25],
      [15, 25],
    ], '#3a3028');
    hammerSabre(p, 22, 3, 16, 25, 0.6);
    shards(p, 16, 22, 9, STONE_HI, 6, 3.6);
    impact(p, 16, 23, 10, GOLD);
  },
});
